import _ from "lodash";
import { buildSelectField } from "@/lib/vueModelUtils";
import { getThingSchemaForId } from "@/lib/thingUtils";
import {
    Attribute,
    SetupPerConfig,
    SetupPerConfigObject,
    ThingSchema,
} from "@/services/things/models";
import { RegistrySchema } from "@/services/registries/registriesAPI";
import { AbstractSchemaConfig, ThingSchemaAdminConfig } from "@/services/schemas/manageSchemasAPI";
import { transformEmptyStringToNull } from "@/lib/utils";
import { RequestProtocol } from "@/services/samples/samplesAPI";

/**
 * Template Attributes do not live on the "local" protocol ThingSchema. If they are not
 * mutated they shouldn't be included on the "local" ThingSchema (they are inherited from
 * the template). If the "template" Attribute is changed from the "template" version, this
 * adds it to the list of protocol ThingSchema Attributes. Attributes that only exist on
 * the protocol ThingSchema should stay regardless of whether they're mutated.
 */
export function getProtocolLevelAttributes(attributes: Attribute[]): Attribute[] {
    return attributes.filter((attr) => {
        return attr.source === "protocol" || attr.source === "overridden";
    });
}

export function buildAttributeRecordObjectToSaveFromList(
    attributes: Attribute[]
): Record<string, Attribute> {
    const record: Record<string, Attribute> = {};
    for (const attribute of attributes) {
        // TODO: do this server side too
        delete attribute["source"];
        delete attribute["modified"];
        record[attribute.name] = attribute;
    }
    return record;
}

export function findThingSchemaByName(
    thingSchemas: ThingSchema[],
    name: string
): ThingSchema | undefined {
    return thingSchemas.find((schema) => schema.name === name);
}

export function parseNamingTemplateAttributes(
    thingSchema: ThingSchema,
    namingTemplate: string
): Attribute[] {
    if (_.isNil(namingTemplate)) {
        return [];
    } else {
        const splits = namingTemplate.split("~");
        const namingTemplateAttrs: Attribute[] = [];
        for (const split of splits) {
            if (isTemplateAttributeRef(split)) {
                const name = parseTemplateAttributeRef(split);
                const attribute = thingSchema.oneTimeAttributes[name];
                if (!_.isNil(attribute)) {
                    namingTemplateAttrs.push(attribute);
                }
            }
        }
        return namingTemplateAttrs;
    }
}

export function parseSetupPerConfigs(thingSchema: ThingSchema): SetupPerConfig[] {
    const perConfig = thingSchema.setup?.per;
    if (_.isNil(perConfig)) {
        return [];
    } else {
        const attributeNames = Object.keys(perConfig);
        return attributeNames.map((attributeName) => {
            const value = perConfig[attributeName] as string | number;
            return {
                attributeName,
                value,
            };
        });
    }
}

export function buildSetupPerConfigObject(
    setupPerConfigs: SetupPerConfig[]
): SetupPerConfigObject | undefined {
    if (setupPerConfigs.length === 0) {
        return undefined;
    }

    const setupPerConfigObject: SetupPerConfigObject = {};
    for (const setupPerConfig of setupPerConfigs) {
        setupPerConfigObject[setupPerConfig.attributeName] = setupPerConfig.value;
    }
    return setupPerConfigObject;
}

export function parseNamingTemplate(thingSchema: ThingSchema): string {
    const namingTemplate = thingSchema.setup?.naming_template;
    if (_.isNil(namingTemplate)) {
        return "";
    } else {
        return namingTemplate.trim();
    }
}

export function parseTemplateAttributeRef(value: string): string {
    return value.substring(1, value.length - 1);
}

export function isTemplateAttributeRef(value: string): boolean {
    return value[0] === "{" && value[value.length - 1] === "}";
}

export function isRegistrySchema(
    schema: ThingSchema | RegistrySchema | RequestProtocol
): schema is RegistrySchema {
    return schema["protocolCode"] === "global";
}

export function isRequestProtocol(
    schema: ThingSchema | RegistrySchema | RequestProtocol
): schema is RequestProtocol {
    return "requestProcessorId" in schema;
}

export function isThingSchema(
    schema: ThingSchema | RegistrySchema | RequestProtocol
): schema is ThingSchema {
    return "protocolCode" in schema && schema["protocolCode"] !== "global";
}

export type SchemaSelectFormCallback = (selectedThingSchema: ThingSchema | undefined) => void;

export function buildSchemaSelectFormField(
    abstractSchemaConfig: AbstractSchemaConfig,
    selectedThingSchema: ThingSchema,
    onChange: SchemaSelectFormCallback
) {
    const schemaOptions = getSchemaOptions(abstractSchemaConfig);

    return buildSelectField({
        name: "Schema",
        options: schemaOptions,
        value: selectedThingSchema ? selectedThingSchema.id : null,
        changed: (value: string) => {
            const selectedThingSchema = getThingSchemaForId(abstractSchemaConfig, value);
            onChange(selectedThingSchema);
        },
    });
}

export function getSchemaOptions(
    abstractSchemaConfig: AbstractSchemaConfig
): { title: string; value: string }[] {
    return abstractSchemaConfig.thingSchemas
        .filter((schema) => {
            return !!schema.protocolCode || !!schema.schemaConfigId;
        })
        .map((schema) => {
            return {
                title: schema.displayName,
                value: schema.id,
            };
        });
}

export function isSchemaNameDisplayDifferent(thingSchema: ThingSchema): boolean {
    const name = thingSchema.name.toLowerCase();
    const displayName = thingSchema.displayName.toLowerCase();
    return name !== displayName;
}

export function isMasterSchema(schema: ThingSchema) {
    return schema.protocolCode === null;
}

function isRoot(schema: ThingSchema) {
    return schema.parentThingSchemaName === "entry";
}

export class SchemaTreeEntry {
    public isInheritedMasterSchema: boolean;
    public key: string;
    public children: SchemaTreeEntry[] = [];

    constructor(public schema: ThingSchema) {
        this.schema = schema;
        // Note: SchemaTreeEntry only contain master ThingSchema if they are _not_ overridden by the Protocol
        this.isInheritedMasterSchema = isMasterSchema(schema);
        this.key = schema.id;
    }
}

export function buildSchemaTree(allThingSchemas: ThingSchema[]) {
    const localSchemas = allThingSchemas.filter((schema) => !isMasterSchema(schema));

    const rootSchemaTreeEntries: SchemaTreeEntry[] = [];
    const allSchemaTreeEntries: SchemaTreeEntry[] = [];

    function findSchemaByName(name: string): ThingSchema | undefined {
        const localSchema = localSchemas.find((schema) => schema.name === name);
        if (localSchema) {
            return localSchema;
        }

        console.warn(`Schema ${name} not found in local schemas; searching templates`);
        return allThingSchemas.find((schema) => schema.name === name);
    }

    function findSchemaTreeEntry(name: string): SchemaTreeEntry | undefined {
        return allSchemaTreeEntries.find((entry) => entry.schema.name === name);
    }

    function addSchemaTreeEntry(schema: ThingSchema): SchemaTreeEntry {
        const schemaTreeEntry = new SchemaTreeEntry(schema);
        allSchemaTreeEntries.push(schemaTreeEntry);
        if (isRoot(schema)) {
            rootSchemaTreeEntries.push(schemaTreeEntry);
        }
        return schemaTreeEntry;
    }

    localSchemas.forEach((schema) => {
        let schemaTreeEntry = findSchemaTreeEntry(schema.name);

        if (!schemaTreeEntry) {
            schemaTreeEntry = addSchemaTreeEntry(schema);
        }

        if (!isRoot(schema)) {
            const parentName = schema.parentThingSchemaName;
            let parentSchemaTreeEntry = findSchemaTreeEntry(parentName);
            if (_.isNil(parentSchemaTreeEntry)) {
                // found the child before the parent.
                const parentSchema = findSchemaByName(parentName);
                if (!_.isNil(parentSchema)) {
                    parentSchemaTreeEntry = addSchemaTreeEntry(parentSchema);
                } else {
                    // something has gone wrong
                    // TODO: create a "missing" SchemaTreeEntry object as a placeholder, show it in the UI
                    parentSchemaTreeEntry = addSchemaTreeEntry({
                        id: (Math.random() * 1000).toString(),
                        name: parentName,
                        displayName: `MISSING: ${parentName}`,
                        parentThingSchemaName: "entry",
                    } as ThingSchema);
                }
            }

            parentSchemaTreeEntry.children.push(schemaTreeEntry);
        }
    });

    return rootSchemaTreeEntries;
}

export type SchemaChanges = {
    attributeDifferences: SchemaAttributeDifferences;
    propertyDifferences: PropertyDifference[];
    isModified: boolean;
};

export type SchemaAttributeDifferences = {
    oneTimeAttributeDifferences: AttributeDifference[];
    timeSeriesAttributeDifferences: AttributeDifference[];
};

export type AttributeDifference = {
    attribute: Attribute;
    isNew: boolean;
    isDeleted: boolean;
    propertyDifferences: PropertyDifference[];
};

export type PropertyDifference = {
    property: string;
    originalValue: unknown;
    modifiedValue: unknown;
};

export function getSchemaChanges(
    thingSchema: ThingSchemaAdminConfig,
    displayName: string,
    parentThingSchema: ThingSchema | null,
    isBenchling: boolean,
    isGeotaggable: boolean,
    isParentBenchling: boolean,
    hasWellPosition: boolean,
    setupPerConfigs: SetupPerConfig[],
    namingTemplate: string | null,
    modifiedOneTimeAttributes: Attribute[],
    modifiedTimeSeriesAttributes: Attribute[]
): SchemaChanges {
    const propertyDifferences: PropertyDifference[] = [];
    addSchemaPropertyDifference("Name", thingSchema.displayName, displayName, propertyDifferences);
    addSchemaPropertyDifference(
        "Sync entities to Benchling?",
        thingSchema.isBenchling,
        isBenchling,
        propertyDifferences
    );
    addSchemaPropertyDifference(
        "Geotaggable?",
        thingSchema.isGeotaggable,
        isGeotaggable,
        propertyDifferences
    );
    addSchemaPropertyDifference(
        "Is parent benchling?",
        thingSchema.isParentBenchling,
        isParentBenchling,
        propertyDifferences
    );
    addSchemaPropertyDifference(
        "Has well position",
        thingSchema.hasWellPosition,
        hasWellPosition,
        propertyDifferences
    );

    const currentParentThingSchemaName = thingSchema.parentThingSchemaName;
    addSchemaPropertyDifference(
        "Parent Schema",
        currentParentThingSchemaName === "entry" ? null : currentParentThingSchemaName,
        parentThingSchema?.name,
        propertyDifferences
    );

    const namingTemplateDifference = compareNamingTemplate(thingSchema, namingTemplate);
    if (namingTemplateDifference) {
        propertyDifferences.push(namingTemplateDifference);
    }

    const attributeDifferences = compareAttributes(
        thingSchema,
        modifiedOneTimeAttributes,
        modifiedTimeSeriesAttributes
    );

    const setupPerConfigDifferences = compareSetupPerConfigs(thingSchema, setupPerConfigs);
    propertyDifferences.push(...setupPerConfigDifferences);

    const isModified =
        attributeDifferences.oneTimeAttributeDifferences.length > 0 ||
        attributeDifferences.timeSeriesAttributeDifferences.length > 0 ||
        propertyDifferences.length > 0;

    return {
        attributeDifferences,
        propertyDifferences,
        isModified,
    };
}

function compareSetupPerConfigs(
    thingSchema: ThingSchemaAdminConfig,
    modifiedSetupPerConfigs: SetupPerConfig[]
): PropertyDifference[] {
    const propertyDifferences: PropertyDifference[] = [];

    const originalSetupPerConfigs = parseSetupPerConfigs(thingSchema);
    for (const originalSetupPerConfig of originalSetupPerConfigs) {
        const modifiedConfigForAttr = modifiedSetupPerConfigs.find(
            (modifiedConfig) =>
                modifiedConfig.attributeName === originalSetupPerConfig.attributeName
        );

        if (_.isNil(modifiedConfigForAttr)) {
            propertyDifferences.push({
                property: originalSetupPerConfig.attributeName,
                originalValue: originalSetupPerConfig.value,
                modifiedValue: null,
            });
        } else if (!_.isEqual(originalSetupPerConfig, modifiedConfigForAttr)) {
            propertyDifferences.push({
                property: originalSetupPerConfig.attributeName,
                originalValue: originalSetupPerConfig.value,
                modifiedValue: modifiedConfigForAttr.value,
            });
        }
    }

    if (modifiedSetupPerConfigs.length > originalSetupPerConfigs.length) {
        for (const modifiedSetupPerConfig of modifiedSetupPerConfigs) {
            const originalConfigForAttr = originalSetupPerConfigs.find(
                (originalConfig) =>
                    originalConfig.attributeName === modifiedSetupPerConfig.attributeName
            );

            if (_.isNil(originalConfigForAttr)) {
                propertyDifferences.push({
                    property: modifiedSetupPerConfig.attributeName,
                    originalValue: null,
                    modifiedValue: modifiedSetupPerConfig.value,
                });
            }
        }
    }

    return propertyDifferences;
}

function compareNamingTemplate(
    thingSchema: ThingSchemaAdminConfig,
    modifiedNamingTemplate: string | null
): PropertyDifference | null {
    const namingTemplate = thingSchema.setup?.naming_template || null;
    if (!_.isEqual(namingTemplate, transformEmptyStringToNull(modifiedNamingTemplate))) {
        return {
            property: "Naming Template",
            originalValue: namingTemplate,
            modifiedValue: modifiedNamingTemplate,
        };
    } else {
        return null;
    }
}

function addSchemaPropertyDifference(
    propertyForDisplay: string,
    originalValue: unknown,
    modifiedValue: unknown,
    propertyDifferences: PropertyDifference[]
) {
    const propertyDifference = compareSchemaProperty(
        propertyForDisplay,
        originalValue,
        modifiedValue
    );
    if (propertyDifference) {
        propertyDifferences.push(propertyDifference);
    }
}

function compareSchemaProperty(
    propertyForDisplay: string,
    originalValue: unknown,
    modifiedValue: unknown
): PropertyDifference | null {
    if (_.isNil(modifiedValue) && _.isNil(modifiedValue)) {
        return null;
    }

    if (!_.isEqual(originalValue, modifiedValue)) {
        return {
            property: propertyForDisplay,
            originalValue,
            modifiedValue,
        };
    }
    return null;
}

function compareAttributes(
    thingSchema: ThingSchemaAdminConfig,
    modifiedOneTimeAttributes: Attribute[],
    modifiedTimeSeriesAttributes: Attribute[]
): SchemaAttributeDifferences {
    const originalOneTimeAttributes = thingSchema.oneTimeAttributeConfigurations;
    const oneTimeAttributeDifferences = getAttributeDifferences(
        originalOneTimeAttributes,
        modifiedOneTimeAttributes
    );

    const originalTimeSeriesAttributes = thingSchema.timeSeriesAttributeConfigurations;
    const timeSeriesAttributeDifferences = getAttributeDifferences(
        originalTimeSeriesAttributes,
        modifiedTimeSeriesAttributes
    );

    return {
        oneTimeAttributeDifferences,
        timeSeriesAttributeDifferences,
    };
}

export function getAttributeDifferences(
    originalAttributes: Attribute[],
    modifiedAttributes: Attribute[]
): AttributeDifference[] {
    const attributeDifferences: AttributeDifference[] = [];

    for (const originalAttr of originalAttributes) {
        const modifiedAttr = modifiedAttributes.find((attr) => attr.name === originalAttr.name);
        if (_.isNil(modifiedAttr)) {
            attributeDifferences.push({
                attribute: originalAttr,
                isDeleted: true,
                isNew: false,
                propertyDifferences: [],
            });
        } else {
            const attributeDifference = getAttributeDifference(originalAttr, modifiedAttr);
            if (!_.isNil(attributeDifference)) {
                attributeDifferences.push(attributeDifference);
            }
        }
    }

    for (const modifiedAttr of modifiedAttributes) {
        const originalForModified = originalAttributes.find(
            (attr) => attr.name === modifiedAttr.name
        );
        if (_.isNil(originalForModified)) {
            attributeDifferences.push({
                attribute: modifiedAttr,
                isNew: true,
                isDeleted: false,
                propertyDifferences: [],
            });
        }
    }

    return attributeDifferences;
}

function getAttributeDifference(
    originalAttribute: Attribute,
    modifiedAttribute: Attribute
): AttributeDifference | null {
    const propertyDifferences: PropertyDifference[] = [];
    getModifiablePropertiesOfAttribute(originalAttribute).forEach((property) => {
        const originalValue = originalAttribute[property];
        const modifiedValue = modifiedAttribute[property];

        // handles `undefined` != `null`
        const bothNil = _.isNil(originalValue) === _.isNil(modifiedValue);
        const valueChanged = !_.isEqual(originalValue, modifiedValue);
        if (!bothNil || valueChanged) {
            propertyDifferences.push({
                property,
                originalValue,
                modifiedValue,
            });
        }
    });

    getModifiablePropertiesOfAttribute(modifiedAttribute)
        .filter((property) => {
            return !originalAttribute.hasOwnProperty(property);
        })
        .forEach((property) => {
            // catch any _new_ properties
            const modifiedValue = modifiedAttribute[property];
            if (!_.isNil(modifiedValue)) {
                propertyDifferences.push({
                    property,
                    originalValue: null,
                    modifiedValue,
                });
            }
        });

    if (propertyDifferences.length === 0) {
        return null;
    }

    return {
        attribute: originalAttribute,
        isNew: false,
        isDeleted: false,
        propertyDifferences: propertyDifferences,
    };
}

function getModifiablePropertiesOfAttribute(attribute: Attribute): string[] {
    return Object.keys(attribute).filter((prop) => {
        return prop !== "modified";
    });
}

export function getDisplayChangedValue(value: unknown): string {
    if (_.isNil(value)) {
        return "null";
    } else {
        return value as string;
    }
}

export function isModified(attribute: Attribute, schemaChanges: SchemaChanges): boolean {
    return (
        isModifiedAttribute(
            attribute,
            schemaChanges.attributeDifferences.oneTimeAttributeDifferences
        ) ||
        isModifiedAttribute(
            attribute,
            schemaChanges.attributeDifferences.timeSeriesAttributeDifferences
        )
    );
}

function isModifiedAttribute(
    attribute: Attribute,
    attributeDifferences: AttributeDifference[]
): boolean {
    const attributeDifferenceForAttr = attributeDifferences.find((attributeDifference) => {
        return attributeDifference.attribute.name === attribute.name;
    });

    return !_.isNil(attributeDifferenceForAttr);
}
